import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  Button,
  Label,
  Dropdown,
  Option,
  Switch,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { 
  STORAGE_KEYS, 
  DEFAULT_SETTINGS, 
  AVAILABLE_LANGUAGES, 
  AVAILABLE_VALIDATORS 
} from '../../constants/config';
import './PreferencesDialog.css';

interface PreferencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PreferencesDialog({ open, onOpenChange }: PreferencesDialogProps) {
  const [language, setLanguage] = useState<string>(DEFAULT_SETTINGS.LANGUAGE);
  const [validator, setValidator] = useState<string>(DEFAULT_SETTINGS.VALIDATOR);
  const [debugMode, setDebugMode] = useState<boolean>(DEFAULT_SETTINGS.DEBUG_MODE);

  // Load from localStorage when dialog opens
  useEffect(() => {
    if (open) {
      const storedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
      if (storedLanguage) {
        setLanguage(storedLanguage);
      } else {
        setLanguage(DEFAULT_SETTINGS.LANGUAGE);
      }

      const storedValidator = localStorage.getItem(STORAGE_KEYS.VALIDATOR);
      if (storedValidator) {
        setValidator(storedValidator);
      } else {
        setValidator(DEFAULT_SETTINGS.VALIDATOR);
      }

      // Boolean-only persistence; anything other than the literal string
      // 'true' is treated as OFF.
      setDebugMode(localStorage.getItem(STORAGE_KEYS.DEBUG_MODE) === 'true');
    }
  }, [open]);

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
    localStorage.setItem(STORAGE_KEYS.VALIDATOR, validator);
    localStorage.setItem(STORAGE_KEYS.DEBUG_MODE, debugMode ? 'true' : 'false');
    
    // Trigger a page reload to apply the changes to the Home page dropdowns
    window.location.reload();
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={() => onOpenChange(false)}
              />
            }
          >
            Preferences
          </DialogTitle>
          <DialogContent className="preferences-content">
            <div className="preference-section">
              <Label htmlFor="language-dropdown">Preferred Language</Label>
              <Dropdown
                id="language-dropdown"
                value={language}
                selectedOptions={[language]}
                onOptionSelect={(_, data) => setLanguage(data.optionValue as string)}
              >
                {AVAILABLE_LANGUAGES.map((lang) => (
                  <Option key={lang} value={lang}>
                    {lang}
                  </Option>
                ))}
              </Dropdown>
            </div>

            <div className="preference-section">
              <Label htmlFor="validator-dropdown">Preferred Validator</Label>
              <Dropdown
                id="validator-dropdown"
                value={validator}
                selectedOptions={[validator]}
                onOptionSelect={(_, data) => setValidator(data.optionValue as string)}
              >
                {AVAILABLE_VALIDATORS.map((v) => (
                  <Option key={v.value} value={v.value}>
                    {v.value}
                  </Option>
                ))}
              </Dropdown>
            </div>

            <div className="preference-section">
              <Switch
                id="debug-mode-switch"
                label="Debug Mode (send a correlation ID so support can inspect this validation request)"
                checked={debugMode}
                onChange={(_, data) => setDebugMode(data.checked)}
              />
            </div>

            <div className="preferences-actions">
              <Button appearance="secondary" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleSave}>
                Save
              </Button>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
